import { Input, type InputRef } from 'antd'
import type { InputProps } from 'antd'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback
} from 'react'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'

// 正規化（全角→半角を包括 / カンマ除去 / 長音符→マイナス）
function toHalfWidthNumericLike(src: string): string {
  return src
    .normalize('NFKC')
    .replace(/,/g, '')
    .replace(/\u30FC/g, '-')
}

// 数値文字列か？（先頭マイナス可／小数点1回まで／空は非数）
function isNumericText(s: string): boolean {
  if (s.trim() === '') return false
  return /^-?\d+(?:\.\d+)?$/.test(s)
}

export type NormalizedInputNumberHandle = {
  validate: () => boolean
  focus: () => void
}

type Props = Omit<
  InputProps,
  | 'value'
  | 'onChange'
  | 'type'
  | 'inputMode'
  | 'onBlur'
  | 'min'
  | 'max'
  | 'step'
> & {
  value?: number | null
  onChange?: (v: number | null) => void
  onInvalidValue?: (message: string) => void
  onValid?: () => void
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  formatText?: (v: number | null) => string
  min?: number | null
  max?: number
  step?: number
}

export const NormalizedInputNumber = forwardRef<
  NormalizedInputNumberHandle,
  Props
>(function NormalizedInputNumber(
  {
    value,
    onChange,
    onInvalidValue,
    onValid,
    onBlur,
    formatText,
    min = 0,
    max,
    step,
    allowClear,
    ...rest
  },
  ref
) {
  const inputRef = useRef<InputRef>(null)

  // 表示用テキスト（RHFの数値とは分離）
  const [text, setText] = useState<string>(value != null ? String(value) : '')

  useEffect(() => {
    const current = value ?? null
    const next =
      current === null ? '' : formatText ? formatText(current) : String(current)

    // 既に同じ表示なら再セットしない（余分な再レンダー防止）
    setText((prev) => (prev === next ? prev : next))
  }, [value, formatText])

  const [isComposing, setIsComposing] = useState(false)

  const isOutOfRange = useCallback(
    (n: number) => (min != null && n < min) || (max != null && n > max),
    [min, max]
  )

  const isOutOfStep = useCallback(
    (n: number) => {
      if (step == null) return false
      const minBase = min ?? 0
      return Math.abs((n - minBase) % step) > 1e-10
    },
    [min, step]
  )

  // 共通バリデーション: commit=true なら値確定 (setText/onChange/onValid)
  const check = useCallback(
    (raw: string, commit: boolean) => {
      if (raw === '') {
        if (commit) {
          onChange?.(null)
          onValid?.()
        }
        return { ok: true as const, value: null as number | null }
      }
      if (!isNumericText(raw)) {
        onInvalidValue?.(VALIDATION_MESSAGES.NUMBER_FORMAT)
        return { ok: false as const }
      }
      const n = parseFloat(raw)
      if (isOutOfRange(n)) {
        onInvalidValue?.(
          min != null && n < min
            ? VALIDATION_MESSAGES.MIN_VALUE(min)
            : VALIDATION_MESSAGES.MAX_VALUE(max!)
        )
        return { ok: false as const }
      }
      if (isOutOfStep(n)) {
        onInvalidValue?.(VALIDATION_MESSAGES.STEP_VALUE(step!))
        return { ok: false as const }
      }

      if (commit) {
        setText(formatText ? formatText(n) : String(n))
        onChange?.(n)
        onValid?.()
      }
      return { ok: true as const, value: n }
    },
    [
      formatText,
      isOutOfRange,
      isOutOfStep,
      min,
      max,
      step,
      onChange,
      onInvalidValue,
      onValid
    ]
  )

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    // IME入力中は正規化しない（そのまま表示に使う）
    if (isComposing) {
      setText(e.target.value)
      return
    }
    const normalized = toHalfWidthNumericLike(e.target.value)
    setText(normalized)

    // allowClear 指定時は空になった瞬間に null を親へ通知する。
    // blur 確定に任せると、クリアボタンを押しても親の value が変わらないため
    // 「表示だけ空・値は残ったまま」になる（value 由来の useEffect も発火せず戻せない）。
    // allowClear 未指定の呼び出し元は従来どおり blur 確定のままにして挙動を変えない。
    if (allowClear && normalized === '') {
      onChange?.(null)
      onValid?.()
      return
    }

    // 入力途中は数値判定のみ通過。範囲外・step違反は親へ渡さない（表示は保持）
    if (isNumericText(normalized)) {
      const n = parseFloat(normalized)
      if (isOutOfRange(n)) return
      if (isOutOfStep(n)) return
      onChange?.(n)
      onValid?.()
    }
  }

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
    e.target.select()
  }

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    // 値確定モード（commit=true）
    check(text, true)
    onBlur?.(e)
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    rest.onKeyDown?.(e)
  }

  useImperativeHandle(ref, () => ({
    // 送信時などに外部からチェックする（表示や値は確定しない）
    validate: () => check(text, false).ok,
    focus: () => inputRef.current?.focus()
  }))

  return (
    <Input
      {...rest}
      ref={inputRef}
      allowClear={allowClear}
      value={text}
      inputMode="decimal"
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onCompositionStart={() => setIsComposing(true)}
      onCompositionEnd={(e) => {
        setIsComposing(false)
        // 確定時に正規化を走らせる
        const normalized = toHalfWidthNumericLike(e.currentTarget.value)
        setText(normalized)
        if (isNumericText(normalized)) {
          const n = parseFloat(normalized)
          if (!isOutOfRange(n) && !isOutOfStep(n)) {
            onChange?.(n)
            onValid?.()
          }
        }
      }}
      onKeyDown={handleKeyDown}
    />
  )
})
